/**
 * Platform Revenue Ledger — admin view of every service-charge event
 * across every channel and both collection models.
 *
 * WITHHELD  = fee kept out of a gateway payout (money already ours,
 *             instantly COLLECTED)
 * INVOICED  = fee owed by merchant (POS or bank-transfer mark-paid),
 *             EXPECTED until they pay their invoice → COLLECTED
 *
 * Backed by /api/admin/platform-revenue/{summary,entries,backfill}.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Filter,
  Download,
  Loader2,
} from 'lucide-react';

const CHANNELS  = ['STOREFRONT', 'WHATSAPP', 'POS', 'MANUAL'];
const STATUSES  = ['EXPECTED', 'COLLECTED', 'REFUNDED', 'WRITTEN_OFF', 'VOIDED'];
const MODELS    = ['WITHHELD', 'INVOICED'];
const PROVIDERS = [
  'PAYSTACK', 'FLUTTERWAVE', 'DVA',
  'POS_INVOICE_PAYSTACK', 'POS_INVOICE_MANUAL',
  'WALLET_DEBIT', 'BANK_TRANSFER', 'MANUAL', 'OTHER'
];

const fmt = (n) => `₦${(Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

// Prefer store name, then merchant's full name, then email, then merchant id.
const merchantDisplay = (m, fallbackId) => {
  if (!m) return fallbackId ? `${fallbackId.slice(0, 8)}…` : '—';
  if (m.store?.name) return m.store.name;
  const full = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  return m.email || (m.id ? `${m.id.slice(0, 8)}…` : '—');
};
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PlatformRevenue = () => {
  const [summary, setSummary]   = useState(null);
  const [entries, setEntries]   = useState([]);
  const [pagination, setPagination] = useState({ total: 0, offset: 0, limit: 50, hasMore: false });
  const [aggregate, setAggregate]   = useState({ totalFeeAmount: 0, totalOrderAmount: 0 });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters]   = useState({
    status: '', channel: '', provider: '', collectionModel: '', merchantId: '', from: '', to: '',
  });
  const [backfillState, setBackfillState] = useState({ running: false, log: null });

  const authHeaders = () => {
    // Match the pattern used by DVASettlements + other admin pages —
    // credentials cookie handles the auth; no bearer needed.
    return { headers: { 'Content-Type': 'application/json' }, credentials: 'include' };
  };

  const loadSummary = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/platform-revenue/summary', authHeaders());
      const d = await r.json();
      if (d.success) setSummary(d);
    } catch (e) {
      console.error('summary load failed', e);
    }
  }, []);

  const loadEntries = useCallback(async (offset = 0) => {
    try {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams({
        offset: String(offset),
        limit: String(pagination.limit),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.channel ? { channel: filters.channel } : {}),
        ...(filters.provider ? { provider: filters.provider } : {}),
        ...(filters.collectionModel ? { collectionModel: filters.collectionModel } : {}),
        ...(filters.merchantId ? { merchantId: filters.merchantId } : {}),
        ...(filters.from ? { from: filters.from } : {}),
        ...(filters.to ? { to: filters.to } : {}),
      });
      const r = await fetch(`/api/admin/platform-revenue/entries?${qs}`, authHeaders());
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Failed to load entries');
      setEntries(d.entries);
      setPagination(d.pagination);
      setAggregate(d.aggregate);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEntries(0); }, [loadEntries]);

  const runBackfillBatch = async (cursor = null) => {
    try {
      setBackfillState((s) => ({ ...s, running: true }));
      const r = await fetch('/api/admin/platform-revenue/backfill', {
        method: 'POST',
        ...authHeaders(),
        body: JSON.stringify({ batchSize: 500, cursorPaidAt: cursor, dryRun: false }),
      });
      const d = await r.json();
      setBackfillState({ running: false, log: d });
      loadSummary();
      loadEntries(0);
    } catch (e) {
      setBackfillState({ running: false, log: { success: false, error: e.message } });
    }
  };

  const exportCSV = () => {
    if (entries.length === 0) return;
    const headers = ['Date','Merchant','Channel','Provider','Model','Status','Order #','Order total','Fee','Who pays','Ref'];
    const rows = entries.map((e) => [
      fmtDate(e.expectedAt),
      merchantDisplay(e.merchant, e.merchantId),
      e.channel,
      e.provider,
      e.collectionModel,
      e.status,
      e.order?.orderNumber || e.order?.whatsappOrderId || e.orderId || '',
      e.orderTotal || '',
      e.serviceChargeAmount,
      e.whoPays || '',
      e.gatewayReference || '',
    ]);
    const csv = [headers, ...rows].map((r) =>
      r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-revenue-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => setFilters({ status: '', channel: '', provider: '', collectionModel: '', merchantId: '', from: '', to: '' });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Platform Revenue</h1>
          <p style={styles.subtitle}>Every service-charge event across every channel and both collection models.</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.iconBtn} onClick={() => { loadSummary(); loadEntries(0); }} title="Refresh">
            <RefreshCw size={16} />
          </button>
          <button style={styles.iconBtn} onClick={() => setShowFilters((s) => !s)} title="Filters">
            <Filter size={16} />
          </button>
          <button style={styles.iconBtn} onClick={exportCSV} title="Export CSV" disabled={entries.length === 0}>
            <Download size={16} />
          </button>
        </div>
      </div>

      {/* Summary — two logical groups, no colored left rules.
          Collected block (3 date ranges) then a subtle separator, then
          Outstanding + Refunded which are conceptually different from
          "money we have". Typography carries the hierarchy, not paint. */}
      {summary && (
        <div style={styles.summarySection}>
          <div style={styles.summaryGroup}>
            <div style={styles.summaryGroupHead}>Collected</div>
            <div style={styles.summaryRow}>
              <SummaryCard label="Today"       amount={summary.today.totalCollected.amount}     sub={eventsLabel(summary.today.totalCollected.count)} />
              <SummaryCard label="This month"  amount={summary.month.totalCollected.amount}     sub={eventsLabel(summary.month.totalCollected.count)} />
              <SummaryCard label="All time"    amount={summary.allTime.totalCollected.amount}   sub={eventsLabel(summary.allTime.totalCollected.count)} strong />
            </div>
          </div>
          <div style={styles.summaryGroup}>
            <div style={styles.summaryGroupHead}>Not yet ours</div>
            <div style={styles.summaryRow}>
              <SummaryCard label="Outstanding"  amount={summary.allTime.invoicedExpected.amount} sub={`${eventsLabel(summary.allTime.invoicedExpected.count)} awaiting merchant payment`} tone="warn" />
              <SummaryCard label="Refunded"     amount={summary.allTime.refunded.amount}         sub={`${eventsLabel(summary.allTime.refunded.count)} reversed`} tone="muted" />
            </div>
          </div>
        </div>
      )}

      {/* Backfill widget */}
      <div style={styles.backfillCard}>
        <div>
          <div style={styles.backfillTitle}>Historical backfill</div>
          <div style={styles.backfillSub}>
            Populates entries for orders that pre-date the ledger. Idempotent + resumable.
          </div>
        </div>
        <div style={styles.backfillActions}>
          {backfillState.log && (
            <span style={styles.backfillStat}>
              Last run: scanned {backfillState.log.scanned}, created {backfillState.log.created}, skipped {backfillState.log.skipped}
              {backfillState.log.nextCursorPaidAt && ' · more to process'}
            </span>
          )}
          <button
            style={styles.primaryBtn}
            onClick={() => runBackfillBatch(backfillState.log?.nextCursorPaidAt || null)}
            disabled={backfillState.running}
          >
            {backfillState.running
              ? <><Loader2 size={14} className="spin" /> Running…</>
              : (backfillState.log?.nextCursorPaidAt ? 'Continue backfill' : 'Run backfill')}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div style={styles.filtersBar}>
          <select style={styles.filterInput} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={styles.filterInput} value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })}>
            <option value="">All channels</option>
            {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={styles.filterInput} value={filters.collectionModel} onChange={(e) => setFilters({ ...filters, collectionModel: e.target.value })}>
            <option value="">All models</option>
            {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select style={styles.filterInput} value={filters.provider} onChange={(e) => setFilters({ ...filters, provider: e.target.value })}>
            <option value="">All providers</option>
            {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input type="date" style={styles.filterInput} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          <input type="date" style={styles.filterInput} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          <input type="text" style={{ ...styles.filterInput, flex: 1, minWidth: 200 }} placeholder="Merchant ID" value={filters.merchantId} onChange={(e) => setFilters({ ...filters, merchantId: e.target.value })} />
          <button style={styles.clearBtn} onClick={clearFilters}>Clear</button>
        </div>
      )}

      {/* Table */}
      <div style={styles.tableWrap}>
        {error && <div style={styles.errorBanner}>{error}</div>}
        {loading && entries.length === 0 ? (
          <div style={styles.centeredLoader}><Loader2 size={20} className="spin" /></div>
        ) : entries.length === 0 ? (
          <div style={styles.emptyState}>No revenue entries match your filters.</div>
        ) : (
          <>
            <div style={styles.aggregateStrip}>
              Showing {entries.length} of {pagination.total.toLocaleString()} entries · Filtered total fee: <strong>{fmt(aggregate.totalFeeAmount)}</strong>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Merchant</th>
                  <th style={styles.th}>Channel</th>
                  <th style={styles.th}>Provider</th>
                  <th style={styles.th}>Model</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Order</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Order total</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Our fee</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} style={styles.tr}>
                    <td style={styles.td}>{fmtDateShort(e.expectedAt)}</td>
                    <td style={styles.td}>{e.merchant?.storeName || e.merchant?.email || <span style={styles.dim}>{e.merchantId.slice(0, 8)}…</span>}</td>
                    <td style={styles.td}><span style={styles.pill}>{e.channel}</span></td>
                    <td style={styles.td}><span style={{ ...styles.pill, ...styles.pillMuted }}>{e.provider}</span></td>
                    <td style={styles.td}><ModelPill model={e.collectionModel} /></td>
                    <td style={styles.td}><StatusPill status={e.status} /></td>
                    <td style={styles.td}>
                      <span style={styles.orderNum}>
                        #{e.order?.orderNumber || e.order?.whatsappOrderId || (e.orderId ? e.orderId.slice(0, 8) : '—')}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(e.orderTotal)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmt(e.serviceChargeAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.paginationBar}>
              <button
                style={styles.pageBtn}
                onClick={() => loadEntries(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0 || loading}
              >← Prev</button>
              <span style={styles.pageInfo}>
                {pagination.offset + 1}–{Math.min(pagination.offset + entries.length, pagination.total)} of {pagination.total.toLocaleString()}
              </span>
              <button
                style={styles.pageBtn}
                onClick={() => loadEntries(pagination.offset + pagination.limit)}
                disabled={!pagination.hasMore || loading}
              >Next →</button>
            </div>
          </>
        )}
      </div>
      <style>{`.spin{animation:spin 0.9s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const SummaryCard = ({ label, amount, sub, tone = 'default', strong = false }) => {
  const amountColor =
    tone === 'warn'  ? '#B45309' :
    tone === 'muted' ? '#6B7280' :
    '#111827';
  return (
    <div style={styles.card}>
      <div style={styles.cardLabel}>{label}</div>
      <div style={{ ...styles.cardAmount, color: amountColor, fontWeight: strong ? 700 : 600 }}>
        {fmt(amount)}
      </div>
      <div style={styles.cardSub}>{sub}</div>
    </div>
  );
};

const eventsLabel = (n) => `${n} event${n === 1 ? '' : 's'}`;

const StatusPill = ({ status }) => {
  const tone = {
    COLLECTED:   { bg: '#e8f5f2', color: '#0f5b52' },
    EXPECTED:    { bg: '#fef3c7', color: '#92400e' },
    REFUNDED:    { bg: '#fef2f2', color: '#b91c1c' },
    WRITTEN_OFF: { bg: '#f3f4f6', color: '#4b5563' },
    VOIDED:      { bg: '#f3f4f6', color: '#4b5563' },
  }[status] || { bg: '#f3f4f6', color: '#4b5563' };
  return <span style={{ ...styles.pill, backgroundColor: tone.bg, color: tone.color }}>{status}</span>;
};

const ModelPill = ({ model }) => {
  const tone = model === 'WITHHELD'
    ? { bg: '#e8f5f2', color: '#0f5b52' }
    : { bg: '#fef3c7', color: '#92400e' };
  return <span style={{ ...styles.pill, backgroundColor: tone.bg, color: tone.color }}>{model}</span>;
};

// ================================ styles ================================
const HAIRLINE = '#EEF2F0';
const INK      = '#111827';
const MUTED    = '#6B7280';
const BRAND    = '#0d7463';

const styles = {
  page: {
    padding: '24px 28px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: INK,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 13, color: MUTED, margin: '4px 0 0' },
  headerActions: { display: 'flex', gap: 8 },
  iconBtn: {
    width: 36, height: 36, borderRadius: 8,
    border: '1px solid ' + HAIRLINE, backgroundColor: '#FFFFFF',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: INK,
  },

  // Two-group summary — Collected (3 cards) then Not-yet-ours (2 cards),
  // each group labelled with a small caps head. No colored borders,
  // no icon squares — typography carries the hierarchy.
  summarySection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    marginBottom: 20,
  },
  summaryGroup: {
    flex: 1,
    minWidth: 340,
  },
  summaryGroupHead: {
    fontSize: 11,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 600,
    marginBottom: 8,
  },
  summaryRow: {
    display: 'flex',
    gap: 1, // 1px gutter for hairline effect between cards
    backgroundColor: HAIRLINE, // shows through gaps as hairlines
    border: '1px solid ' + HAIRLINE,
    borderRadius: 10,
    overflow: 'hidden',
  },
  card: {
    flex: 1,
    minWidth: 0,
    padding: '14px 16px',
    backgroundColor: '#FFFFFF',
  },
  cardLabel: {
    fontSize: 11,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 600,
    marginBottom: 6,
  },
  cardAmount: {
    fontSize: 22,
    lineHeight: '28px',
    letterSpacing: '-0.01em',
  },
  cardSub: { fontSize: 11, color: MUTED, marginTop: 4 },

  backfillCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    border: '1px solid ' + HAIRLINE,
    marginBottom: 20,
  },
  backfillTitle: { fontSize: 14, fontWeight: 600, color: INK },
  backfillSub: { fontSize: 12, color: MUTED, marginTop: 2 },
  backfillActions: { display: 'flex', alignItems: 'center', gap: 12 },
  backfillStat: { fontSize: 12, color: MUTED },
  primaryBtn: {
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: BRAND,
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  filtersBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: 12,
    backgroundColor: '#FFFFFF',
    border: '1px solid ' + HAIRLINE,
    borderRadius: 10,
    marginBottom: 16,
  },
  filterInput: {
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid ' + HAIRLINE,
    fontSize: 13,
    color: INK,
    backgroundColor: '#FFFFFF',
  },
  clearBtn: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid ' + HAIRLINE,
    backgroundColor: '#FFFFFF',
    color: MUTED,
    fontSize: 13,
    cursor: 'pointer',
  },

  tableWrap: {
    backgroundColor: '#FFFFFF',
    border: '1px solid ' + HAIRLINE,
    borderRadius: 10,
    overflow: 'hidden',
  },
  errorBanner: {
    padding: 12,
    backgroundColor: '#FEF2F2',
    color: '#B91C1C',
    fontSize: 13,
  },
  centeredLoader: { padding: 40, textAlign: 'center', color: MUTED },
  emptyState: { padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 },
  aggregateStrip: {
    padding: '10px 14px',
    borderBottom: '1px solid ' + HAIRLINE,
    fontSize: 12,
    color: MUTED,
    backgroundColor: '#FAFBFC',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: 600,
    borderBottom: '1px solid ' + HAIRLINE,
    backgroundColor: '#FAFBFC',
  },
  tr: { borderBottom: '1px solid ' + HAIRLINE },
  td: { padding: '10px 14px', fontSize: 13, color: INK, verticalAlign: 'middle' },
  dim: { color: MUTED, fontFamily: 'ui-monospace, monospace', fontSize: 12 },
  pill: {
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 4,
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  pillMuted: { backgroundColor: '#F9FAFB', color: MUTED },
  orderNum: { fontFamily: 'ui-monospace, monospace', fontSize: 12, color: INK },

  paginationBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    borderTop: '1px solid ' + HAIRLINE,
  },
  pageBtn: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid ' + HAIRLINE,
    backgroundColor: '#FFFFFF',
    color: INK,
    fontSize: 13,
    cursor: 'pointer',
  },
  pageInfo: { fontSize: 12, color: MUTED },
};

export default PlatformRevenue;
